import { Card, CardContent } from '@design-system/Card';
import { List, ListItem } from '@design-system/List';
import { Link } from '@design-system/Link';
import type { SubscriptionStatus } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { isSubscriptionCancelled, isSubscriptionActiveState, isSubscriptionPaused } from '../utils/smpHelpers';
import {
  SMP_ADD_REMOVE_PRODUCTS_PATH,
  SMP_PAUSE_SUBSCRIPTION_PATH,
  SMP_CANCEL_SUBSCRIPTION_PATH,
  SMP_REACTIVATE_PATH,
  SMP_RESUME_SUBSCRIPTION_PATH,
} from '@/constants/paths';

interface SMPLandingPageManageSectionProps {
  subscriptionStatus: SubscriptionStatus;
  hasPersonalizedSupplementRecommendations: boolean;
  isEligibleForSupplements: boolean;
  onSkipOrderClick: () => void;
}

export const SMPLandingPageManageSection = ({
  subscriptionStatus,
  hasPersonalizedSupplementRecommendations,
  isEligibleForSupplements,
  onSkipOrderClick,
}: SMPLandingPageManageSectionProps) => {
  const isCancelled = isSubscriptionCancelled(subscriptionStatus);
  const isActiveState = isSubscriptionActiveState(subscriptionStatus);
  const isPaused = isSubscriptionPaused(subscriptionStatus);

  return (
    <div className="flex flex-col gap-6" data-testid="smp-manage-section">
      <h2
        className="typography-h2 text-primary px-1"
        data-testid="smp-manage-title"
      >
        Manage Subscription
      </h2>

      <Card data-testid="smp-manage-card">
        <CardContent className="p-0" data-testid="smp-manage-content">
          <List data-testid="smp-manage-list">
            {isActiveState && isEligibleForSupplements && hasPersonalizedSupplementRecommendations && (
              <>
                <ListItem asChild data-testid="smp-manage-addRemove">
                  <Link to={SMP_ADD_REMOVE_PRODUCTS_PATH}>
                    Add or Remove Products
                  </Link>
                </ListItem>

                <ListItem asChild data-testid="smp-manage-skipOrder">
                  <button type="button" onClick={onSkipOrderClick}>
                    Skip Next Order
                  </button>
                </ListItem>
              </>
            )}

            {isActiveState && (
              <>
                {isPaused ? (
                  <ListItem asChild data-testid="smp-manage-resume">
                    <Link to={SMP_RESUME_SUBSCRIPTION_PATH}>
                      Resume Subscription
                    </Link>
                  </ListItem>
                ) : (
                  <ListItem asChild data-testid="smp-manage-pause">
                    <Link to={SMP_PAUSE_SUBSCRIPTION_PATH}>
                      Pause Subscription
                    </Link>
                  </ListItem>
                )}

                <ListItem asChild data-testid="smp-manage-cancel">
                  <Link to={SMP_CANCEL_SUBSCRIPTION_PATH}>
                    Cancel Subscription
                  </Link>
                </ListItem>
              </>
            )}

            {isCancelled && (
              <ListItem asChild data-testid="smp-manage-reactivate">
                <Link to={SMP_REACTIVATE_PATH}>
                  Reactivate Subscription
                </Link>
              </ListItem>
            )}
          </List>
        </CardContent>
      </Card>
    </div>
  );
};
